import { useEffect, useState } from "react";
import {
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Button,
  Card,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  LinearProgress,
  Autocomplete,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  DialogTitle,
  DialogActions,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import BulkSearchHCPBasic from "../../assets/template/HCP_basic.csv";
import BulkSearchHCPAdvance from "../../assets/template/HCP_advance.csv";
import BulkSearchHCOBasic from "../../assets/template/HCO_basic.csv";
import BulkSearchHCOAdvance from "../../assets/template/HCO_advance.csv";
import BulkVendorSearch from "../../assets/template/vendor_basic.csv";
import "./styles.css";
import Dropzone from "../dropzone/Dropzone";
import HistoryIcon from "@mui/icons-material/History";
import { BULK_SEARCH_CONSTANTS, BUTTONS } from "../../Constant/BulkOpsContant";
import {
  createRecordAwareChunks,
  uploadChunk,
} from "../../service/bulkServices";
import { useHistory } from "react-router-dom";
import Progress from "../bulk-operations/Progress";
import ResultsProcessing from "../bulk-operations/ResultsProcessing";
import api from "../../service/api";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import DownloadLOV from "../../assets/template/Search_LOV.csv";

const BulkSearch = () => {
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchIn, setSearchIn] = useState("eph");
  const [uploadPhase, setUploadPhase] = useState(""); // "uploading", "processing"
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const [vendorCountries, setVendorCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [searchFor, setSearchFor] = useState("HCP");
  const [searchType, setSearchType] = useState("basic");
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
  const [duplicateRecords, setDuplicateRecords] = useState([]);
  const [processedFile, setProcessedFile] = useState(null);
  const [countries, setCountries] = useState([]);
  const [showDownloadPopup, setShowDownloadPopup] = useState(false);
  const [selectedDownloadCountry, setSelectedDownloadCountry] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const history = useHistory();

  // Function to detect and convert file to UTF-8 encoding
  const convertToUTF8 = async (file) => {
    try {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const arrayBuffer = e.target.result;
            const uint8Array = new Uint8Array(arrayBuffer);

            // Try to decode as UTF-8 first
            let text;
            try {
              const decoder = new TextDecoder("utf-8", { fatal: true });
              text = decoder.decode(uint8Array);
            } catch (utfError) {
              // If UTF-8 fails, try common encodings
              const encodings = ["windows-1252", "iso-8859-1", "utf-16"];
              let decoded = false;

              for (const encoding of encodings) {
                try {
                  const decoder = new TextDecoder(encoding);
                  text = decoder.decode(uint8Array);
                  decoded = true;
                  console.log(`File decoded using ${encoding} encoding`);
                  break;
                } catch (err) {
                  continue;
                }
              }

              if (!decoded) {
                // Fallback to UTF-8 with replacement characters
                const decoder = new TextDecoder("utf-8", { fatal: false });
                text = decoder.decode(uint8Array);
              }
            }

            // Create a new UTF-8 encoded file
            const utf8Blob = new Blob([text], {
              type: "text/csv;charset=utf-8",
            });

            // Copy file properties
            Object.defineProperty(utf8Blob, "name", {
              value: file.name,
              writable: false,
            });
            Object.defineProperty(utf8Blob, "lastModified", {
              value: file.lastModified,
              writable: false,
            });

            resolve(utf8Blob);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    } catch (error) {
      console.error("Error converting file to UTF-8:", error);
      return file; // Return original file if conversion fails
    }
  };

  // Function to remove duplicate records from CSV file
  const removeDuplicateRecords = async (
    file,
    searchFor,
    searchIn,
    searchType,
  ) => {
    try {
      const csvContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      const lines = csvContent.split("\n");
      if (lines.length === 0) return file;

      const headers = lines[0];
      const dataRows = lines.slice(1).filter((line) => line.trim() !== "");

      if (dataRows.length === 0) return file;

      // Determine the key column for duplicate detection based on search type
      let keyColumn;
      if (searchIn === "vendor") {
        keyColumn = "OneKeyId";
      } else if (searchIn === "eph" && searchType === "basic") {
        keyColumn = searchFor === "HCP" ? "HCPEnterpriseId" : "HCOEnterpriseId";
      } else {
        // For advance search, don't remove duplicates
        return file;
      }

      const headerArray = headers.split(",").map((h) => h.trim());
      const keyColumnIndex = headerArray.indexOf(keyColumn);

      if (keyColumnIndex === -1) {
        // Key column not found, return original file
        return file;
      }

      // Track seen values and remove duplicates
      const seenValues = new Set();
      const uniqueRows = [];
      const duplicateRows = [];
      let duplicatesRemoved = 0;

      for (const row of dataRows) {
        const columns = row.split(",");
        const keyValue = columns[keyColumnIndex]?.trim();

        if (keyValue && !seenValues.has(keyValue)) {
          seenValues.add(keyValue);
          uniqueRows.push(row);
        } else if (keyValue) {
          duplicatesRemoved++;
          duplicateRows.push({ row, keyValue, columns });
        } else {
          // Keep rows with empty key values
          uniqueRows.push(row);
        }
      }

      if (duplicatesRemoved > 0) {
        // Create new CSV content with unique rows
        const newCsvContent = [headers, ...uniqueRows].join("\n");

        // Create new file blob
        const newFile = new Blob([newCsvContent], { type: "text/csv" });
        // Copy file properties
        Object.defineProperty(newFile, "name", {
          value: file.name,
          writable: false,
        });
        Object.defineProperty(newFile, "lastModified", {
          value: file.lastModified,
          writable: false,
        });

        return {
          file: newFile,
          hasDuplicates: true,
          duplicateRecords: duplicateRows.map((dup) => ({
            keyValue: dup.keyValue,
            rowData: dup.columns,
          })),
          duplicatesRemoved,
          keyColumn,
        };
      }

      return {
        file,
        hasDuplicates: false,
        duplicateRecords: [],
        duplicatesRemoved: 0,
      };
    } catch (error) {
      console.error("Error removing duplicates:", error);
      return file;
    }
  };

  // Function to count CSV rows
  const getCsvRowCount = async (file) => {
    try {
      const csvContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      const lines = csvContent.split("\n");
      const dataRows = lines.slice(1).filter((line) => line.trim() !== "");
      return dataRows.length;
    } catch (error) {
      console.error("Error counting CSV rows:", error);
      return 0;
    }
  };

  const handleChangeRadio = (event) => {
    setSearchIn(event.target.value);
  };

  const proceedWithUpload = async (fileToProcess) => {
    setLoading(true);
    setShowLoadingDialog(true);
    setUploadPhase("uploading");
    const csvRowCount = await getCsvRowCount(fileToProcess);
    const chunks = await createRecordAwareChunks(
      fileToProcess,
      "bulkSearch",
      searchFor,
      searchIn === "eph" ? searchType : "basic",
      searchIn,
    );

    const totalChunks = chunks.length;
    setUploadProgress({ current: 0, total: totalChunks });
    let uniqueID;
    let createdAt;
    for (let i = 0; i < chunks.length; i++) {
      const getData = await uploadChunk(
        chunks[i],
        i,
        totalChunks,
        searchIn,
        selectedFile,
        uniqueID,
        "BULKSEARCH",
        createdAt,
        searchFor,
        chunks[i].lastRowNo,
        chunks[i].firstRowNo,
        searchIn === "eph" ? searchType : "basic",
        searchIn === "eph"
          ? "null"
          : selectedCountry
            ? selectedCountry
            : "null",
          "null"
      );
      if (i === 0) {
        uniqueID = getData.uploadId;
        createdAt = getData.createdAt;
        sessionStorage.setItem("searchUploadId", uniqueID);
        sessionStorage.setItem("searchIn", searchIn);
        sessionStorage.setItem("totalCsvRows", csvRowCount);
        sessionStorage.setItem(
          "searchType",
          searchIn === "eph" ? searchType : "basic",
        );
      }
      const progressCurrent = i + 1;
      setUploadProgress({ current: progressCurrent, total: totalChunks });
      if (i < chunks.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setUploadPhase("processing");
    setUploadProgress({ current: 0, total: 0 });

    setTimeout(() => {
      setShowLoadingDialog(false);
      setLoading(false);
      setSelectedFile(null);
      setUploadPhase("");
      setUploadProgress({ current: 0, total: 0 });

      history.push("/bulk-search/results", {
        uploadId: uniqueID,
        fileName: selectedFile.name,
        uploadSuccess: true,
        uploadType: "bulk-search",
        createdAt: createdAt,
        totalRows: csvRowCount,
      });
    }, 20000);
  };

  const handleConfirmDuplicates = async () => {
    setShowDuplicatePopup(false);
    try {
      await proceedWithUpload(processedFile);
    } catch (error) {
      setError(error.message || "An error occurred during upload");
      setLoading(false);
      setShowLoadingDialog(false);
      setUploadPhase("");
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleCancelDuplicates = () => {
    setShowDuplicatePopup(false);
    setDuplicateRecords([]);
    setProcessedFile(null);
    setLoading(false);
    setShowLoadingDialog(false);
    setUploadPhase("");
    setUploadProgress({ current: 0, total: 0 });
  };

  const handleClickUpload = async () => {
    if (!selectedFile) {
      return;
    }

    setLoading(true);
    setShowLoadingDialog(true);
    setUploadPhase("uploading");
    setUploadProgress({ current: 0, total: 0 });

    try {
      let fileToProcess = selectedFile;

      // For advance search, convert file to UTF-8 to handle special characters correctly
      if (searchType === "advance") {
        console.log("Converting file to UTF-8 for advance search...");
        fileToProcess = await convertToUTF8(selectedFile);
      }

      // Remove duplicate records for basic search before processing
      const result = await removeDuplicateRecords(
        fileToProcess,
        searchFor,
        searchIn,
        "basic",
      );

      if (result.hasDuplicates) {
        // Show duplicate popup
        setDuplicateRecords(result.duplicateRecords);
        setProcessedFile(result.file);
        setShowDuplicatePopup(true);
        setShowLoadingDialog(false);
        return;
      }

      // No duplicates, proceed with upload
      if (searchType === "basic") {
        await proceedWithUpload(result.file);
      } else {
        await proceedWithUpload(fileToProcess);
      }
    } catch (error) {
      setError(error.message || "An error occurred during upload");
      setLoading(false);
      setShowLoadingDialog(false);
      setUploadPhase("");
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  useEffect(() => {
    try {
      const result = fetchVendorCountries();
      result.then((data) => setVendorCountries(data));
    } catch (error) {
      console.error("Error fetching vendor countries:", error);
    }
  }, []);

  const fetchVendorCountries = async () => {
    const countryData = await api.getSelectedDCRCountry();
    const processedCountries = countryData.data
      .filter((country) => country.split("#")[1] === "IQVIA")
      .map((country) => country.replace("#IQVIA", "").trim());

    // Remove duplicates and sort alphabetically
    const uniqueCountries = [...new Set(processedCountries)]
      .filter((country) => country.length > 0)
      .sort();

    return uniqueCountries;
  };

  const fetchCountries = async () => {
    const countryData = await api.getSelectedDCRCountry();
    const vendorPrefixes = [
      "#IQVIA_US",
      "#IQVIA",
      "#Veeva Network",
      "#OPEN-DATA",
    ];

    const processedCountries = countryData.data.map((country) => {
      for (const prefix of vendorPrefixes) {
        if (country.includes(prefix)) {
          return country.replace(prefix, "").trim();
        }
      }
      return country.trim();
    });

    // Remove duplicates and sort alphabetically
    const uniqueCountries = [...new Set(processedCountries)]
      .filter((country) => country.length > 0)
      .sort();

    return uniqueCountries;
  };
  useEffect(() => {
    try {
      const result = fetchCountries();
      result.then((data) => setCountries(data));
    } catch (error) {
      console.error("Error fetching vendor countries:", error);
    }
  }, []);

  const handleDownloadClick = () => {
    setShowDownloadPopup(true);
  };

  // Proper CSV parsing function to handle quoted values with commas
  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    let i = 0;

    while (i < line.length) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i += 2;
        } else {
          // Toggle quotes
          inQuotes = !inQuotes;
          i++;
        }
      } else if (char === "," && !inQuotes) {
        // Field separator
        result.push(current.trim());
        current = "";
        i++;
      } else {
        current += char;
        i++;
      }
    }

    // Add the last field
    result.push(current.trim());
    return result;
  };

  const handleDownloadSubmit = async () => {
    if (selectedDownloadCountry) {
      setDownloadLoading(true);
      try {
        // Fetch the CSV file content
        const response = await fetch(DownloadLOV);
        const csvText = await response.text();

        // Parse CSV content using proper CSV parser
        const lines = csvText.split("\n");
        const headers = parseCSVLine(lines[0]);

        // Find the country column index
        const countryColumnIndex = headers.findIndex((header) =>
          header.toLowerCase().includes("country"),
        );

        if (countryColumnIndex === -1) {
          setErrorMessage("Country column not found in CSV file.");
          setShowErrorPopup(true);
          setDownloadLoading(false);
          return;
        }

        // Filter data based on selected country
        const filteredData = [];
        filteredData.push(headers); // Add headers first

        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim()) {
            const row = parseCSVLine(lines[i]);
            if (
              row[countryColumnIndex] &&
              row[countryColumnIndex]
                .toLowerCase()
                .includes(selectedDownloadCountry.toLowerCase())
            ) {
              filteredData.push(row);
            }
          }
        }

        // Convert to Excel format and download
        if (filteredData.length > 1) {
          downloadAsExcel(
            filteredData,
            `${selectedDownloadCountry}_LOV_Data.xlsx`,
          );
          setShowDownloadPopup(false);
          setSelectedDownloadCountry("");
        } else {
          setErrorMessage(
            `No data found for country: ${selectedDownloadCountry}`,
          );
          setShowErrorPopup(true);
        }
      } catch (error) {
        console.error("Error processing download:", error);
        setErrorMessage("Error processing download. Please try again.");
        setShowErrorPopup(true);
      } finally {
        setDownloadLoading(false);
      }
    }
  };

  const downloadAsExcel = (data, filename) => {
    // Create Excel-compatible CSV content with proper quoting
    const csvContent = data
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell || "");
            // Quote the cell if it contains comma, quote, or newline
            if (
              cellStr.includes(",") ||
              cellStr.includes('"') ||
              cellStr.includes("\n")
            ) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(","),
      )
      .join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename.replace(".xlsx", ".csv"));
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadCancel = () => {
    setShowDownloadPopup(false);
    setSelectedDownloadCountry("");
  };

  const handleErrorClose = () => {
    setShowErrorPopup(false);
    setErrorMessage("");
  };

  return (
    <div className="container body-height extra-margin-top-less mb-4">
      <Card sx={{ maxWidth: 890 }} className="mx-auto">
        <div className="m-5">
          <Box className="d-flex justify-content-between align-items-center mb-4">
            <Typography variant="h5" className="fw-bold _wg-dynamic-text">
              {BULK_SEARCH_CONSTANTS.HEADING}
            </Typography>
            <Button
              variant="text"
              startIcon={<HistoryIcon />}
              size="small"
              color="error"
              onClick={() => history.push("/upload-history")}
            >
              {BUTTONS.VIEW_HISTORY}
            </Button>
          </Box>
          <Typography
            variant="body1"
            paragraph
            className="sub-heading-bs _wg-dynamic-text"
          >
            {BULK_SEARCH_CONSTANTS.SUB_HEADING}
          </Typography>
          <FormControl className="my-3 d-flex labels-radio-bs">
            <FormLabel id="demo-row-radio-buttons-group-label">
              {BULK_SEARCH_CONSTANTS.RADIO_GROUP.HEADING}
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              onChange={handleChangeRadio}
              value={searchIn}
            >
              {BULK_SEARCH_CONSTANTS.RADIO_GROUP.OPTIONS &&
                Object.values(BULK_SEARCH_CONSTANTS.RADIO_GROUP.OPTIONS).map(
                  (option) => (
                    <Tooltip
                      key={option.VALUE}
                      title={
                        option.VALUE === "vendor"
                          ? "Currently only IQVIA_OUS is supported for this type of search"
                          : ""
                      }
                      arrow
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            whiteSpace: "nowrap",
                            maxWidth: "none",
                            fontSize: "0.75rem",
                          },
                        },
                      }}
                    >
                      <FormControlLabel
                        value={option.VALUE}
                        control={<Radio />}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {option.LABEL}{" "}
                            {option.VALUE === "vendor" && (
                              <InfoIcon
                                sx={{ fontSize: 16, color: "#D31710" }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </Tooltip>
                  ),
                )}
            </RadioGroup>
            {searchIn === "vendor" && vendorCountries?.length > 0 && (
              <Autocomplete
                className="vendor-country-bs"
                disablePortal
                id="combo-box-demo"
                options={vendorCountries}
                sx={{ mt: 2 }}
                onChange={(event, newValue) => {
                  setSelectedCountry(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Vendor Country" />
                )}
              />
            )}
          </FormControl>
          <FormControl
            style={{
              width: "30%",
            }}
            className="mb-3 labels-radio-bs-2"
          >
            <FormLabel>Search for:</FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              onChange={(event) => setSearchFor(event.target.value)}
              value={searchFor}
            >
              <FormControlLabel
                value={"HCP"}
                control={<Radio />}
                label={"HCP"}
              />
              <FormControlLabel
                value={"HCO"}
                control={<Radio />}
                label={"HCO"}
              />
            </RadioGroup>
          </FormControl>
          {searchIn === "eph" && (
            <FormControl className="mb-3 labels-radio-bs-2">
              <FormLabel>Search Type:</FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                onChange={(event) => setSearchType(event.target.value)}
                value={searchType}
              >
                <FormControlLabel
                  value={"basic"}
                  control={<Radio />}
                  label={"Basic"}
                />
                <FormControlLabel
                  value={"advance"}
                  control={<Radio />}
                  label={"Advanced"}
                />
              </RadioGroup>
            </FormControl>
          )}
          <Dropzone
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            error={error}
            setError={setError}
            templateUrl={
              searchIn === "vendor"
                ? BulkVendorSearch
                : searchFor === "HCO"
                  ? searchType === "basic"
                    ? BulkSearchHCOBasic
                    : BulkSearchHCOAdvance
                  : searchType === "basic"
                    ? BulkSearchHCPBasic
                    : BulkSearchHCPAdvance
            }
            allowedFormats=".csv"
            typeUpload="bulk-search"
            searchType={searchType}
            searchIn={searchIn}
            handleDownloadClick={handleDownloadClick}
          />
          <div className="text-end">
            <Button
              variant="contained"
              color="error"
              className="_wg-dynamic-text mt-4 upload-btn rounded-md"
              disabled={
                !selectedFile ||
                error ||
                loading ||
                (searchIn === "vendor" && !selectedCountry)
              }
              onClick={handleClickUpload}
            >
              {loading ? (
                <CircularProgress size={24} style={{ color: "#ffff" }} />
              ) : (
                BULK_SEARCH_CONSTANTS.SUBMIT_BUTTON
              )}
            </Button>
          </div>
        </div>
      </Card>
      <Dialog
        open={showLoadingDialog}
        maxWidth="sm"
        fullWidth
        disableBackdropClick
        disableEscapeKeyDown
      >
        <DialogContent
          className="upload-loading-dialog"
          sx={{ py: 4, textAlign: "center" }}
        >
          {uploadPhase === "uploading" && (
            <Progress
              open={showLoadingDialog}
              uploadProgress={uploadProgress}
            />
          )}
          {uploadPhase === "processing" && <ResultsProcessing />}
        </DialogContent>
      </Dialog>

      {/* Duplicate Records Popup */}
      <Dialog
        open={showDuplicatePopup}
        maxWidth="md"
        disableBackdropClick
        disableEscapeKeyDown
        style={{ marginTop: "80px" }}
      >
        <DialogContent sx={{ p: 4 }} className="validate-popup">
          <Typography variant="h6" sx={{ mb: 2, color: "#d32f2f" }}>
            <WarningAmberRoundedIcon /> Duplicate Records Found
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            We found {duplicateRecords.length} duplicate record(s) in your file.
            These records will be <strong>removed</strong> for processing. Do you
             {" "}<strong>wish</strong> to continue?
          </Typography>

          <Box sx={{ maxHeight: 300, overflow: "auto", mb: 3 }}>
            <Accordion style={{ border: "1px solid gainsboro" }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="duplicate-records-content"
                id="duplicate-records-header"
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Duplicate Records ({duplicateRecords.length} found)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {duplicateRecords.map((record, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      mb: 1,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Record {index + 1}: {record.keyValue}
                    </Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancelDuplicates}
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDuplicates}
              sx={{ minWidth: 100 }}
            >
              Confirm & Continue
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Download Template Popup */}
      <Dialog
        open={showDownloadPopup}
        onClose={handleDownloadCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ color: "#D31710", fontWeight: "bold" }}
          style={{ fontSize: "18px" }}
        >
          Download Reference LOVs
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            fullWidth
            sx={{ mt: 2 }}
            options={countries}
            value={selectedDownloadCountry || null}
            onChange={(event, newValue) => {
              setSelectedDownloadCountry(newValue || "");
            }}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Country"
                placeholder="Search countries..."
                variant="outlined"
              />
            )}
            noOptionsText="No countries found"
            clearOnEscape
            filterSelectedOptions={false}
            includeInputInList
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDownloadCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDownloadSubmit}
            variant="contained"
            color="error"
            disabled={!selectedDownloadCountry || downloadLoading}
          >
            {downloadLoading ? (
              <CircularProgress size={20} style={{ color: "#fff" }} />
            ) : (
              "Download"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Popup */}
      <Dialog
        open={showErrorPopup}
        onClose={handleErrorClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#d32f2f", fontWeight: "bold" }}>
          Error
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleErrorClose} variant="contained" color="error">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BulkSearch;
